import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import Filters from "../components/Filters";
import RecipeList from "../components/RecipeList";
import { useUser } from "../contexts/UserContext";
import { getRecipes, getSavedRecipeIdsForUser } from "../services/recipeServices";
import fuzzyMatch from "../utils/fuzzyMatch";

const sortExtractors = {
  cookTime: (recipe) => recipe.cookTime ?? 0,
  created: (recipe) => recipe.created?.toMillis?.() ?? 0,
  ingredients: (recipe) =>
    recipe.ingredients?.reduce?.(
      (total, section) => total + (section.items?.length ?? 0),
      0,
    ) ?? 0,
};

const ListRecipes = () => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useUser();
  const [savedRecipeIds, setSavedRecipeIds] = useState([]);

  useEffect(() => {
    const fetchRecipes = async () => {
      try {
        const fetchedRecipes = await getRecipes();
        setRecipes(fetchedRecipes);
      } catch (err) {
        setError("Unable to load recipes. Please try again later.");
        console.error("Failed to fetch recipes", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecipes();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchSavedRecipes = async () => {
      if (!user) {
        if (isMounted) {
          setSavedRecipeIds([]);
        }
        return;
      }

      try {
        const savedIds = await getSavedRecipeIdsForUser(user.id);
        if (isMounted) {
          setSavedRecipeIds(savedIds);
        }
      } catch (err) {
        console.error("Failed to fetch saved recipes", err);
        if (isMounted) {
          setSavedRecipeIds([]);
        }
      }
    };

    fetchSavedRecipes();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const filters = useMemo(() => {
    const params = Object.fromEntries([...searchParams]);

    if (!params.sort) {
      params.sort = "created-desc";
    }

    return params;
  }, [searchParams]);

  const savedRecipeIdSet = useMemo(
    () => new Set(savedRecipeIds),
    [savedRecipeIds],
  );

  const filteredRecipes = useMemo(() => {
    const { cuisine, course, created, sort, saved } = filters;
    const searchTerm = (filters.q ?? filters.ingredient ?? "").trim();

    const collectIngredientNames = (recipe) =>
      recipe.ingredients?.flatMap?.((section) =>
        section?.items?.map?.((item) => item?.name).filter(Boolean) ?? [],
      ) ?? [];

    const matchesCuisine = (recipe) =>
      !cuisine || recipe.cuisine.toLowerCase() === cuisine.toLowerCase();

    const matchesCourse = (recipe) =>
      !course || recipe.course.toLowerCase() === course.toLowerCase();

    const isCreatedByUser = (recipe) =>
      !created || !user || recipe.userId.toLowerCase() === user.id.toLowerCase();

    const isSavedByUser = (recipe) =>
      !saved || !user || savedRecipeIdSet.has(recipe.id);

    const matchesSearchTerm = (recipe) => {
      if (!searchTerm) {
        return true;
      }

      const keywordTargets = [recipe.title, ...collectIngredientNames(recipe)];

      return keywordTargets.some((target) => fuzzyMatch(target, searchTerm));
    };

    const filtered = recipes.filter(
      (recipe) =>
        matchesCuisine(recipe) &&
        matchesCourse(recipe) &&
        isCreatedByUser(recipe) &&
        isSavedByUser(recipe) &&
        matchesSearchTerm(recipe),
    );

    if (!sort) {
      return filtered;
    }

    const [field, direction] = sort.split("-");
    const getValue = sortExtractors[field] ?? (() => 0);
    const directionFactor = direction === "asc" ? 1 : -1;

    return [...filtered].sort((left, right) => {
      const leftValue = getValue(left);
      const rightValue = getValue(right);

      return (leftValue - rightValue) * directionFactor;
    });
  }, [filters, recipes, user, savedRecipeIdSet]);

  const handleSelectRecipe = useCallback(
    (id) => {
      navigate(`/recipe/${id}`);
    },
    [navigate],
  );

  const handleFilterChange = useCallback(
    (newFilters) => {
      setSearchParams(newFilters);
    },
    [setSearchParams],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <p className="text-2xl text-slate-500">Loading recipes...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-6">
        <p className="text-2xl text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="mb-6 text-3xl font-bold text-primary">Recipes</h1>
        <Filters filters={filters} onChange={handleFilterChange} user={user} />
      </div>
      <RecipeList recipes={filteredRecipes} onSelectRecipe={handleSelectRecipe} />
    </div>
  );
};

export default ListRecipes;

